import { periodForInstant, readablePeriod, UNREADABLE_DATE, type Timeframe } from '../../functions-src/lib/calendar'

// User roles
export type UserRole = 'admin' | 'viewer' | 'new_user'

// Goal comparison operators
export type GoalOperator = 'gte' | 'lte' | 'range'

// Timeframe for goals (defined by the business calendar)
export type { Timeframe }

// User type
export interface User {
  id: string
  slack_id: string
  slack_team_id: string
  email?: string
  name: string
  avatar_url?: string
  role: UserRole
  created_at: number
  updated_at: number
}

// Business area for categorizing metrics
export interface BusinessArea {
  id: string
  name: string
  display_order: number
  is_active: boolean
  created_at: number
}

// Metric definition
export interface Metric {
  id: string
  name: string
  description?: string
  unit?: string // e.g., '%', '$', '#'
  area_id?: string | null // Business area category
  area_name?: string | null // Joined from business_areas
  display_order: number
  is_active: boolean
  is_hidden?: boolean // Hidden from scorecard but still accepts data
  created_by: string
  created_at: number
  updated_at: number
}

// Goal (target for a metric)
export interface Goal {
  id: string
  metric_id: string
  owner_id: string
  timeframe: Timeframe
  period_start: number
  period_end: number | null
  is_recurring: boolean
  operator: GoalOperator
  target_value: number
  target_min?: number // for range operator
  target_max?: number // for range operator
  created_by: string
  created_at: number
  updated_at: number
}

// Metric data point (time series)
export interface MetricValue {
  id: string
  metric_id: string
  value: number
  recorded_at: number
  source?: string // e.g., 'n8n', 'manual', 'api'
  metadata?: string // JSON
  created_at: number
}

// Dispatch status for audit logs (individual dispatch attempts)
export type DispatchLogStatus = 'pending' | 'sent' | 'accepted' | 'rejected' | 'failed'

// Dispatch status for bonus lifecycle (overall state machine)
export type BonusDispatchStatus = 'pending' | 'dispatched' | 'accepted' | 'rejected' | 'failed' | 'reconciled'

// Bonus configuration (rule definition - inherits goal's timeframe automatically)
// Note: This is now stored in bonus_configs table after migration 005
export interface BonusConfig {
  id: string
  goal_id: string
  user_id: string
  amount: number
  currency: string
  archived_at?: number | null // NULL = active, timestamp = archived
  // Legacy fields (deprecated, kept for backward compat)
  is_achieved?: boolean
  paid_at?: number
  hr_webhook_sent?: boolean
  hr_response?: string
  created_at: number
  updated_at: number
}

// Bonus Instance (per-period record with dispatch tracking)
// Note: Each bonus config can have multiple instances (one per period)
export interface BonusInstance {
  id: string
  config_id: string
  period_id: string // Format: {config_id}_{period_start} for idempotency
  period_start: number
  period_end: number
  is_met: boolean
  metric_value_at_close?: number
  dispatch_status: BonusDispatchStatus
  last_dispatch_at?: number
  dispatch_attempts: number
  next_retry_at?: number
  retry_count: number
  max_retries: number
  last_error?: string
  created_at: number
  updated_at: number
}

// Combined view for UI (instance with config and goal details)
export interface BonusInstanceWithDetails extends BonusInstance {
  config_amount: number
  config_currency: string
  goal_id: string
  goal_metric_name?: string
  goal_timeframe?: Timeframe
  user_id: string
  user_name?: string
  user_email?: string
}

// Legacy Bonus interface (alias to BonusConfig for backward compatibility)
// @deprecated Use BonusConfig for new code
export interface Bonus {
  id: string
  goal_id: string
  user_id: string
  amount: number
  currency: string
  is_achieved: boolean
  computed_achieved?: boolean // Calculated from latest metric value
  paid_at?: number
  hr_webhook_sent: boolean
  hr_response?: string
  // Dispatch tracking (Phase 01)
  dispatch_status?: BonusDispatchStatus
  last_dispatch_at?: number
  dispatch_attempts?: number
  // Retry mechanism (Phase 05)
  next_retry_at?: number
  retry_count?: number
  max_retries?: number
  last_error?: string
  created_at: number
  updated_at: number
}

// Bonus Dispatch Log (one row per dispatch attempt)
export interface BonusDispatchLog {
  id: string
  bonus_id: string // Legacy: references bonus_configs.id
  instance_id?: string // New: references bonus_instances.id
  idempotency_key: string
  request_url: string
  request_body: string
  request_headers?: string
  http_status?: number
  response_body?: string
  response_time_ms?: number
  dispatch_status: DispatchLogStatus
  error_message?: string
  initiated_by: string
  initiated_at: number
}

// Bonus Audit Event (state transitions)
export type BonusAuditEventType =
  | 'created'
  | 'achieved'
  | 'dispatch_initiated'
  | 'dispatch_sent'
  | 'dispatch_accepted'
  | 'dispatch_rejected'
  | 'dispatch_failed'
  | 'manually_marked_paid'
  | 'reconciled'

export interface BonusAuditEvent {
  id: string
  bonus_id: string // Legacy: references bonus_configs.id
  instance_id?: string // New: references bonus_instances.id
  event_type: BonusAuditEventType
  previous_status?: string
  new_status?: string
  actor_id: string
  actor_role?: string
  reason?: string
  metadata?: string
  created_at: number
}

// Scorecard row for display (joined data)
export interface ScorecardRow {
  metric: Metric
  goal: Goal
  owner: User
  bonus?: Bonus // only visible to owner
  current_value?: number
  is_goal_met: boolean
  historical_values: {
    period_start: number
    period_end: number
    value: number
    is_goal_met: boolean
  }[]
}

// API response wrapper
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

// Auth state
export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
}

// Operator display symbols
export const OPERATOR_SYMBOLS: Record<GoalOperator, string> = {
  gte: '≥',
  lte: '≤',
  range: '↔',
}

// Timeframe labels
export const TIMEFRAME_LABELS: Record<Timeframe, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  biannual: '6 Months',
  yearly: 'Yearly',
}

// Comprehensive scorecard types (Bloom-style)
export type GoalStatus = 'met' | 'missed' | 'pending'

export interface ComprehensivePeriodValue {
  period_start: number
  period_end: number
  label: string
  value: number | null
  status: GoalStatus
}

export interface ComprehensiveMetricRow {
  metric_id: string
  goal_id: string | null
  metric_name: string
  unit: string | null
  display_order: number
  area_id: string | null
  area_name: string | null
  owner_id: string | null
  owner_name: string | null
  owner_avatar: string | null
  operator: GoalOperator | null
  target_value: number | null
  target_min: number | null
  target_max: number | null
  timeframe: Timeframe | null
  goal_start: number | null // Unix timestamp when goal was assigned
  values: ComprehensivePeriodValue[]
}

export interface ComprehensiveScorecardData {
  periods: { label: string; start: number; end: number }[]
  metrics: ComprehensiveMetricRow[]
}

export type LabelType = 'date_range' | 'period'
export type ColumnCount = 4 | 8 | 12 | 18 | 24 | 30 | 36

// Period helpers: thin wrappers over the business calendar (null/undefined timeframe means monthly)
function periodOf(timestamp: number, timeframe: Timeframe | null | undefined) {
  return periodForInstant(timestamp, timeframe ?? 'monthly')
}

// Format period label based on timeframe
export function formatPeriodLabel(timestamp: number, timeframe: Timeframe | null | undefined): string {
  return readablePeriod(timestamp, timeframe ?? 'monthly')?.label ?? UNREADABLE_DATE
}

// Get unique period key for grouping values by timeframe
export function getPeriodKey(timestamp: number, timeframe: Timeframe | null | undefined): string {
  return periodOf(timestamp, timeframe).key
}

// Get the start timestamp of a period given any timestamp within that period
export function getPeriodStart(timestamp: number, timeframe: Timeframe | null | undefined): number {
  return periodOf(timestamp, timeframe).start
}

// Aggregate values by period (averaging multiple data points within the same period)
export function aggregateValuesByPeriod(
  values: { value: number; recorded_at: number }[],
  timeframe: Timeframe | null | undefined
): { value: number; recorded_at: number; count: number }[] {
  if (!values.length) return []

  // Group values by period
  const periodMap = new Map<string, { values: number[]; timestamps: number[] }>()

  for (const v of values) {
    // A value whose time cannot be read has no period, so it can't go on a chart
    if (!readablePeriod(v.recorded_at, timeframe ?? 'monthly')) continue
    const key = getPeriodKey(v.recorded_at, timeframe)
    if (!periodMap.has(key)) {
      periodMap.set(key, { values: [], timestamps: [] })
    }
    periodMap.get(key)!.values.push(v.value)
    periodMap.get(key)!.timestamps.push(v.recorded_at)
  }

  // Calculate averages and use period start timestamp for consistent labeling
  const aggregated: { value: number; recorded_at: number; count: number }[] = []

  for (const [, data] of periodMap) {
    const avg = data.values.reduce((a, b) => a + b, 0) / data.values.length
    // Use any timestamp from this period to calculate the period start
    const periodStart = getPeriodStart(data.timestamps[0], timeframe)
    aggregated.push({
      value: Math.round(avg * 100) / 100, // Round to 2 decimal places
      recorded_at: periodStart, // Use period start for consistent labeling
      count: data.values.length,
    })
  }

  // Sort by timestamp
  return aggregated.sort((a, b) => a.recorded_at - b.recorded_at)
}

// ==================== ACTION PLANS ====================

// Action Plan status
export type ActionPlanStatus = 'draft' | 'active' | 'completed' | 'expired'

// Action Plan for tracking steps toward goal achievement
export interface ActionPlan {
  id: string
  goal_id: string
  owner_id: string
  title: string
  description?: string
  expected_outcome?: string
  status: ActionPlanStatus
  start_date?: number
  target_date?: number
  completed_at?: number
  archived_at?: number | null
  created_by: string
  created_at: number
  updated_at: number
}

// Reflection on an action plan (one per user per plan)
export interface ActionPlanReflection {
  id: string
  action_plan_id: string
  user_id: string
  user_name: string
  user_avatar?: string
  what_worked?: string
  what_didnt_work?: string
  general_thoughts?: string
  effectiveness_rating: number
  created_at: number
  updated_at: number
}

// Extended with joined data for display
export interface ActionPlanWithOwner extends ActionPlan {
  owner_name: string
  owner_avatar?: string
}

// ==================== NOTIFICATIONS ====================

// Unviewed action plan with metric/goal context for notifications page
export interface UnviewedActionPlan extends ActionPlanWithOwner {
  metric_id: string
  metric_name: string
  timeframe: Timeframe
}
